import React, { useLayoutEffect, useMemo, useState } from 'react'
import { ChartingView } from './ChartingViewToggle.component'
import {
  getPerioSurfaceForPart,
  PerioSitesByTooth,
} from '@utils/perio-line.utils'
import {
  isUpperTooth,
  PERIO_LEFT_EDGE_FRAC,
  PERIO_REF_STROKE,
  PERIO_RIGHT_EDGE_FRAC,
  PERIO_SERIES_A_STROKE,
  PERIO_SERIES_B_STROKE,
  PERIO_VIEW_H,
  perioMmToY,
} from './tooth-perio-line.geometry'
import './ToothPerioRowConnectors.css'

export type PerioConnectorPart = 'base' | 'palatal' | 'lingual'

interface ToothPerioRowConnectorsProps {
  containerRef: React.RefObject<HTMLElement | null>
  toothIds: number[]
  part: PerioConnectorPart
  view: ChartingView
  perioSitesByTooth: PerioSitesByTooth
}

interface AnchorRect {
  toothId: number
  left: number
  top: number
  width: number
  height: number
}

interface ConnectorSegment {
  x1: number
  y1: number
  x2: number
  y2: number
}

/**
 * Interproximal segments only. P1/P5 on each tooth are always 0 mm (CEJ),
 * so connectors join neighbors on the baseline without moving clinical sites.
 */
const ToothPerioRowConnectors = ({
  containerRef,
  toothIds,
  part,
  view,
  perioSitesByTooth,
}: ToothPerioRowConnectorsProps): React.ReactElement | null => {
  const [anchors, setAnchors] = useState<AnchorRect[]>([])
  const [box, setBox] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) {
      return undefined
    }

    const measure = (): void => {
      const containerRect = container.getBoundingClientRect()
      const nextAnchors: AnchorRect[] = []
      for (const toothId of toothIds) {
        const el = container.querySelector(
          `[data-perio-anchor="${toothId}-${part}"]`,
        )
        if (!(el instanceof HTMLElement)) {
          continue
        }
        const rect = el.getBoundingClientRect()
        if (rect.width <= 0 || rect.height <= 0) {
          continue
        }
        nextAnchors.push({
          toothId,
          left: rect.left - containerRect.left,
          top: rect.top - containerRect.top,
          width: rect.width,
          height: rect.height,
        })
      }
      setAnchors(nextAnchors)
      setBox({
        width: container.clientWidth,
        height: container.clientHeight,
      })
    }

    measure()
    const resizeObserver = new ResizeObserver(() => {
      measure()
    })
    resizeObserver.observe(container)
    const frameId = window.requestAnimationFrame(() => {
      measure()
    })
    return () => {
      window.cancelAnimationFrame(frameId)
      resizeObserver.disconnect()
    }
  }, [containerRef, toothIds, part, perioSitesByTooth, view])

  const hasCompareB = useMemo((): boolean => {
    return toothIds.some((toothId) => {
      const sites = perioSitesByTooth[toothId]
      if (!sites) {
        return false
      }
      const surface = getPerioSurfaceForPart(part, toothId, view)
      return surface === 'facial'
        ? Boolean(sites.facialB)
        : Boolean(sites.lingualB)
    })
  }, [toothIds, part, view, perioSitesByTooth])

  const segments = useMemo((): ConnectorSegment[] => {
    if (anchors.length < 2 || box.width <= 0) {
      return []
    }
    const byId = new Map(anchors.map((a) => [a.toothId, a]))
    const result: ConnectorSegment[] = []

    for (let i = 0; i < toothIds.length - 1; i += 1) {
      const leftId = toothIds[i]
      const rightId = toothIds[i + 1]
      const leftAnchor = byId.get(leftId)
      const rightAnchor = byId.get(rightId)
      if (!leftAnchor || !rightAnchor) {
        continue
      }

      const x1 = leftAnchor.left + leftAnchor.width * PERIO_RIGHT_EDGE_FRAC
      const x2 = rightAnchor.left + rightAnchor.width * PERIO_LEFT_EDGE_FRAC
      const y1 =
        leftAnchor.top +
        (perioMmToY(0, isUpperTooth(leftId)) / PERIO_VIEW_H) * leftAnchor.height
      const y2 =
        rightAnchor.top +
        (perioMmToY(0, isUpperTooth(rightId)) / PERIO_VIEW_H) *
          rightAnchor.height

      result.push({ x1, y1, x2, y2 })
    }
    return result
  }, [anchors, box.width, toothIds])

  if (box.width <= 0 || box.height <= 0 || segments.length === 0) {
    return null
  }

  return (
    <svg
      className="tooth-perio-row-connectors"
      width={box.width}
      height={box.height}
      viewBox={`0 0 ${box.width} ${box.height}`}
      aria-hidden="true"
    >
      {segments.map((seg, i) => (
        <line
          key={`ref-${i}`}
          x1={seg.x1}
          y1={seg.y1}
          x2={seg.x2}
          y2={seg.y2}
          stroke={PERIO_REF_STROKE}
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {segments.map((seg, i) => (
        <line
          key={`a-${i}`}
          x1={seg.x1}
          y1={seg.y1}
          x2={seg.x2}
          y2={seg.y2}
          stroke={PERIO_SERIES_A_STROKE}
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {hasCompareB &&
        segments.map((seg, i) => (
          <line
            key={`b-${i}`}
            x1={seg.x1}
            y1={seg.y1}
            x2={seg.x2}
            y2={seg.y2}
            stroke={PERIO_SERIES_B_STROKE}
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        ))}
    </svg>
  )
}

export default ToothPerioRowConnectors
